import { useMutation } from '@tanstack/react-query';
import { Pause, Play, Plus, Search, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';

import { ApiError, type Instance, type Sample, api, unwrap, useDefinitions, useInstanceAction, useInstances, useInvalidateFleet, useMeta } from '@/api';
import { Sparkline } from '@/components/Charts';
import { Badge, Button, Dialog, Empty, Field, Input, PageHeader, ProtocolBadge, Select, Spinner, StatusDot, Switch, cx } from '@/components/ui';
import { formatInterval, formatValue } from '@/lib/format';
import { useSampleStream } from '@/lib/live';

/** Latest sample + a short numeric trail per instance, re-rendered at most once a second. */
function useLiveTable() {
	const latest = useRef(new Map<string, Sample>());
	const trails = useRef(new Map<string, number[]>());
	const [, setTick] = useState(0);
	useSampleStream(null, ({ instanceId, sample }) => {
		latest.current.set(instanceId, sample);
		const first = Object.values(sample.values).find((v) => typeof v === 'number') as number | undefined;
		if (first !== undefined) {
			const t = trails.current.get(instanceId) ?? [];
			t.push(first);
			if (t.length > 40) t.shift();
			trails.current.set(instanceId, t);
		}
	});
	useEffect(() => {
		const h = setInterval(() => setTick((n) => n + 1), 1000);
		return () => clearInterval(h);
	}, []);
	return { latest: latest.current, trails: trails.current };
}

export function Instances() {
	const instances = useInstances();
	const defs = useDefinitions();
	const action = useInstanceAction();
	const invalidate = useInvalidateFleet();
	const live = useLiveTable();
	const [q, setQ] = useState('');
	const [def, setDef] = useState('');
	const [state, setState] = useState('');
	const [selected, setSelected] = useState<Set<string>>(new Set());
	const [bulkOpen, setBulkOpen] = useState(false);

	const defName = (id: string) => defs.data?.find((d) => d.id === id)?.name ?? id;
	const fieldsOf = (id: string) => defs.data?.find((d) => d.id === id)?.fields ?? [];
	const rows = useMemo(
		() =>
			(instances.data ?? []).filter(
				(i) =>
					(!q || `${i.name} ${i.slug} ${i.tags.join(' ')}`.toLowerCase().includes(q.toLowerCase())) &&
					(!def || i.definitionId === def) &&
					(!state || i.status === state),
			),
		[instances.data, q, def, state],
	);

	const many = async (ids: string[], op: 'start' | 'stop' | 'delete') => {
		const results = await Promise.allSettled(
			ids.map((id) =>
				op === 'delete'
					? unwrap(api.DELETE('/api/v1/instances/{id}', { params: { path: { id } } }))
					: unwrap(api.POST(`/api/v1/instances/{id}/${op}`, { params: { path: { id } } })),
			),
		);
		const failed = results.filter((r) => r.status === 'rejected');
		if (failed.length) toast.error(`${failed.length} of ${ids.length} failed: ${(failed[0] as PromiseRejectedResult).reason.message}`);
		else toast.success(`${op === 'delete' ? 'Deleted' : op === 'start' ? 'Started' : 'Stopped'} ${ids.length} sensor${ids.length === 1 ? '' : 's'}`);
		setSelected(new Set());
		invalidate();
	};

	if (instances.isPending) return <Spinner />;
	const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));

	return (
		<>
			<PageHeader
				title="Sensors"
				subtitle={`${instances.data?.length ?? 0} configured · live values update every second`}
				actions={
					<>
						<Button onClick={() => setBulkOpen(true)}>Bulk create</Button>
						<Link to="/instances/new">
							<Button variant="primary">
								<Plus className="size-4" /> New sensor
							</Button>
						</Link>
					</>
				}
			/>
			<div className="mb-3 flex flex-wrap items-center gap-2">
				<div className="relative w-full min-w-48 sm:w-auto sm:flex-1">
					<Search className="pointer-events-none absolute top-2 left-2.5 size-4 text-fg-muted" />
					<Input placeholder="Search name, slug or tag" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8" />
				</div>
				<Select value={def} onChange={(e) => setDef(e.target.value)} className="sm:w-56">
					<option value="">All definitions</option>
					{defs.data?.map((d) => (
						<option key={d.id} value={d.id}>
							{d.name}
						</option>
					))}
				</Select>
				<Select value={state} onChange={(e) => setState(e.target.value)} className="sm:w-40">
					<option value="">Any status</option>
					{['running', 'stopped', 'crashed', 'invalid', 'starting'].map((s) => (
						<option key={s} value={s}>
							{s}
						</option>
					))}
				</Select>
			</div>

			{selected.size > 0 && (
				<div className="mb-3 flex flex-wrap items-center gap-2 rounded-md border border-accent/40 bg-accent-soft px-3 py-2 text-sm">
					<span className="font-medium">{selected.size} selected</span>
					<Button size="sm" onClick={() => many([...selected], 'start')}>
						<Play className="size-3.5" /> Start
					</Button>
					<Button size="sm" onClick={() => many([...selected], 'stop')}>
						<Pause className="size-3.5" /> Stop
					</Button>
					<Button size="sm" variant="danger" onClick={() => confirm(`Delete ${selected.size} sensors?`) && many([...selected], 'delete')}>
						<Trash2 className="size-3.5" /> Delete
					</Button>
					<Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
						Clear
					</Button>
				</div>
			)}

			{rows.length === 0 ? (
				<Empty
					title={instances.data?.length ? 'No sensors match the filters' : 'No sensors yet'}
					action={
						<Link to="/definitions">
							<Button variant="primary">Browse the catalogue</Button>
						</Link>
					}
				>
					Each sensor is an instance of a built-in definition with its own parameters, served over its protocol.
				</Empty>
			) : (
				<div className="overflow-x-auto rounded-lg border border-border bg-surface">
					<table className="w-full min-w-[52rem] text-sm">
						<thead className="border-b border-border text-left text-xs text-fg-muted">
							<tr>
								<th className="w-8 px-3 py-2">
									<input
										type="checkbox"
										aria-label="Select all"
										checked={allSelected}
										onChange={() => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)))}
									/>
								</th>
								<th className="px-3 py-2 font-medium">Sensor</th>
								<th className="px-3 py-2 font-medium">Exposed as</th>
								<th className="px-3 py-2 font-medium">Latest</th>
								<th className="px-3 py-2 font-medium">Trend</th>
								<th className="px-3 py-2 font-medium">Every</th>
								<th className="px-3 py-2 font-medium">Status</th>
								<th className="px-3 py-2" />
							</tr>
						</thead>
						<tbody className="divide-y divide-border">
							{rows.map((i) => (
								<Row
									key={i.id}
									i={i}
									defName={defName(i.definitionId)}
									fields={fieldsOf(i.definitionId)}
									latest={live.latest.get(i.id) ?? i.latest ?? undefined}
									trail={live.trails.get(i.id) ?? []}
									selected={selected.has(i.id)}
									onSelect={(on) => {
										const s = new Set(selected);
										if (on) s.add(i.id);
										else s.delete(i.id);
										setSelected(s);
									}}
									onToggle={() =>
										action.mutate(
											{ id: i.id, action: i.state === 'running' ? 'stop' : 'start' },
											{ onError: (e) => toast.error(e.message) },
										)
									}
								/>
							))}
						</tbody>
					</table>
				</div>
			)}
			<BulkCreate open={bulkOpen} onClose={() => setBulkOpen(false)} />
		</>
	);
}

function Row({
	i,
	defName,
	fields,
	latest,
	trail,
	selected,
	onSelect,
	onToggle,
}: {
	i: Instance;
	defName: string;
	fields: { key: string; label: string; unit: string; decimals: number; kind: string }[];
	latest?: Sample;
	trail: number[];
	selected: boolean;
	onSelect: (on: boolean) => void;
	onToggle: () => void;
}) {
	const shown = fields.slice(0, 2);
	return (
		<tr className={cx('transition hover:bg-muted/50', selected && 'bg-accent-soft/50')}>
			<td className="px-3 py-2">
				<input type="checkbox" aria-label={`Select ${i.name}`} checked={selected} onChange={(e) => onSelect(e.target.checked)} />
			</td>
			<td className="max-w-64 px-3 py-2">
				<Link to={`/instances/${i.id}`} className="block truncate font-medium hover:text-accent">
					{i.name}
				</Link>
				<span className="block truncate text-xs text-fg-muted">
					<code className="font-mono">{i.slug}</code> · {defName}
				</span>
			</td>
			<td className="px-3 py-2">
				<div className="flex flex-wrap gap-1">
					{i.bindings.length ? i.bindings.map((b) => <ProtocolBadge key={b.id} protocol={b.config.protocol} muted={!b.enabled} />) : <ProtocolBadge protocol="http" muted />}
				</div>
			</td>
			<td className="px-3 py-2 font-mono text-xs whitespace-nowrap tabular-nums">
				{i.state === 'running' && latest
					? shown.map((f) => (
							<div key={f.key}>
								<span className="text-fg-muted">{f.label}: </span>
								{formatValue(f, latest.values[f.key])}
							</div>
						))
					: '—'}
			</td>
			<td className="px-3 py-2">{i.state === 'running' ? <Sparkline values={trail} /> : null}</td>
			<td className="px-3 py-2 text-xs whitespace-nowrap text-fg-muted">{formatInterval(i.sampleIntervalMs)}</td>
			<td className="px-3 py-2" title={i.statusDetail ?? undefined}>
				<StatusDot status={i.status} />
				{i.status === 'running' && !i.online && (
					<Badge tone="warn" className="ml-1.5">
						offline
					</Badge>
				)}
			</td>
			<td className="px-3 py-2 text-right">
				<Button size="sm" variant="ghost" onClick={onToggle} aria-label={i.state === 'running' ? 'Stop' : 'Start'}>
					{i.state === 'running' ? <Pause className="size-3.5" /> : <Play className="size-3.5" />}
				</Button>
			</td>
		</tr>
	);
}

function BulkCreate({ open, onClose }: { open: boolean; onClose: () => void }) {
	const defs = useDefinitions();
	const meta = useMeta();
	const navigate = useNavigate();
	const invalidate = useInvalidateFleet();
	const [definitionId, setDefinitionId] = useState('');
	const [count, setCount] = useState(10);
	const [namePattern, setNamePattern] = useState('Sensor {n}');
	const [interval, setIntervalMs] = useState(1000);
	const [autostart, setAutostart] = useState(true);
	const [errors, setErrors] = useState<Record<string, string>>({});
	const create = useMutation({
		mutationFn: () =>
			unwrap(api.POST('/api/v1/instances/bulk', { body: { definitionId: definitionId || defs.data![0]!.id, count, namePattern, sampleIntervalMs: interval, autostart } })),
		onSuccess: (r) => {
			toast.success(`Created ${r.items.length} sensors`);
			invalidate();
			onClose();
			navigate('/instances');
		},
		onError: (e) => {
			if (e instanceof ApiError) setErrors(e.fieldErrors());
			toast.error(e.message);
		},
	});
	return (
		<Dialog open={open} onClose={onClose} title="Bulk create sensors">
			<form
				className="flex flex-col gap-3"
				onSubmit={(e) => {
					e.preventDefault();
					setErrors({});
					create.mutate();
				}}
			>
				<Field label="Definition" error={errors['/definitionId']}>
					<Select value={definitionId} onChange={(e) => setDefinitionId(e.target.value)}>
						{defs.data?.map((d) => (
							<option key={d.id} value={d.id}>
								{d.name} ({d.id})
							</option>
						))}
					</Select>
				</Field>
				<div className="grid grid-cols-2 gap-3">
					<Field label="How many" error={errors['/count']} hint={`Up to ${meta.data?.limits.maxBulk ?? 500}`}>
						<Input type="number" min={1} value={count} onChange={(e) => setCount(Number(e.target.value))} />
					</Field>
					<Field label="Sample every (ms)" error={errors['/sampleIntervalMs']}>
						<Input type="number" min={meta.data?.limits.minIntervalMs ?? 100} value={interval} onChange={(e) => setIntervalMs(Number(e.target.value))} />
					</Field>
				</div>
				<Field label="Name pattern" error={errors['/namePattern']} hint="{n} becomes 1, 2, 3… Slugs are derived from names.">
					<Input value={namePattern} onChange={(e) => setNamePattern(e.target.value)} />
				</Field>
				<Switch checked={autostart} onChange={setAutostart} label="Start immediately" />
				<p className="text-xs text-fg-muted">Each sensor gets the definition's default parameters, a random seed and its own address on the expected protocol.</p>
				<div className="flex justify-end gap-2">
					<Button onClick={onClose}>Cancel</Button>
					<Button variant="primary" type="submit" disabled={create.isPending}>
						Create {count}
					</Button>
				</div>
			</form>
		</Dialog>
	);
}
